import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, MessageSquare, Mail, MapPin, Loader2, ChevronDown, ShieldCheck, ChevronRight, Info } from "lucide-react";
import { API } from "@/config";

/* ConversationPanel — unified SMS + email thread for one reservation.
 * Drivers use it to message guests without ever seeing phone/email; the
 * backend relays through Twilio/Resend. Admins get the same thread plus
 * the underlying contact + consent details (thread.contact). */

const TEMPLATES = [
  { key: "on_the_way", label: "Driver on the way", body: "Hi [Customer First Name], this is Enchanted Gear Rentals. Your driver is on the way with your rental. Estimated arrival is approximately [ETA]." },
  { key: "arrived", label: "Driver arrived", body: "Hi [Customer First Name], your Enchanted Gear Rentals driver has arrived at the designated resort lobby for your scheduled handoff." },
  { key: "cant_locate", label: "Unable to locate customer", body: "Hi [Customer First Name], your Enchanted Gear Rentals driver is at the designated handoff location but has not been able to locate you. Please reply as soon as possible." },
  { key: "pickup_reminder", label: "Pickup reminder", body: "Hi [Customer First Name], this is a reminder that your Enchanted Gear Rentals pickup is scheduled for today during your selected pickup window." },
];

const fmtWhen = (x) => x ? new Date(x).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : "";

const STATUS_STYLES = {
  sent: "text-[#B9C8D5]",
  delivered: "text-emerald-300",
  failed: "text-red-400",
  skipped: "text-[#F4A93A]",
  received: "text-[#FFD166]",
};

/* ── Message detail modal ── */
const MessageDetailModal = ({ msg, onClose }) => {
  if (!msg) return null;
  const statusOk = msg.status === "sent" || msg.status === "delivered";
  const hasFullBody = msg.full_body && msg.full_body.trim().length > 0;
  const bodyToShow = hasFullBody ? msg.full_body : msg.body;

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A1628] border border-[#FFD166]/20 rounded-2xl p-5 max-w-lg w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {msg.channel === "sms"
              ? <MessageSquare className="w-4 h-4 text-[#FFD166]" />
              : <Mail className="w-4 h-4 text-[#FFD166]" />}
            <span className="font-medium text-[#F6F1E4]">{msg.subject || "Automated Message"}</span>
          </div>
          <button onClick={onClose} className="text-[#B9C8D5] hover:text-[#FFD166]"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-[#B9C8D5]/60 uppercase tracking-widest text-[9px] mb-0.5">Channel</p>
              <p className="text-[#F6F1E4] uppercase">{msg.channel}</p>
            </div>
            <div>
              <p className="text-[#B9C8D5]/60 uppercase tracking-widest text-[9px] mb-0.5">Status</p>
              <p className={`font-medium ${statusOk ? "text-emerald-400" : msg.status === "failed" ? "text-red-400" : "text-[#B9C8D5]"}`}>
                {statusOk ? "Sent ✓" : msg.status === "failed" ? "Failed ✗" : msg.status}
              </p>
            </div>
            <div>
              <p className="text-[#B9C8D5]/60 uppercase tracking-widest text-[9px] mb-0.5">Sent</p>
              <p className="text-[#F6F1E4]">{fmtWhen(msg.created_at)}</p>
            </div>
            {msg.destination_hint && (
              <div>
                <p className="text-[#B9C8D5]/60 uppercase tracking-widest text-[9px] mb-0.5">Recipient</p>
                <p className="text-[#F6F1E4]">{msg.destination_hint}</p>
              </div>
            )}
          </div>

          <div className="border-t border-[#FFD166]/10 pt-3">
            <p className="text-[#B9C8D5]/60 uppercase tracking-widest text-[9px] mb-1.5">Message Content</p>
            {bodyToShow ? (
              <div className="bg-black/30 rounded-xl p-3 text-[#F6F1E4] whitespace-pre-wrap break-words leading-relaxed text-xs border border-[#FFD166]/10">
                {bodyToShow}
              </div>
            ) : (
              <div className="flex items-start gap-2 bg-[#FFD166]/5 border border-[#FFD166]/15 rounded-xl p-3">
                <Info className="w-3.5 h-3.5 text-[#FFD166]/70 shrink-0 mt-0.5" />
                <p className="text-xs text-[#B9C8D5]">
                  Full message body is not stored for this entry. SMS bodies will be captured for messages sent going forward.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Bubble = ({ m }) => {
  const [detailOpen, setDetailOpen] = useState(false);
  const isCustomer = m.sender === "customer";
  const isSystem = m.sender === "system";
  const senderLabel = isCustomer ? "Customer" : isSystem ? (m.subject || "Automated") : m.sender === "admin" ? (m.sender_name || "Admin") : (m.sender_name || "Driver");
  const statusOk = m.status === "sent" || m.status === "delivered";
  return (
    <>
      {detailOpen && <MessageDetailModal msg={m} onClose={() => setDetailOpen(false)} />}
      <div className={`flex ${isCustomer ? "justify-start" : isSystem ? "justify-center" : "justify-end"}`} data-testid={`msg-${m.id}`}>
        {isSystem ? (
          // System / automated messages: clickable pill to open detail modal
          <div
            className="max-w-[88%] w-full cursor-pointer group"
            data-testid={`system-msg-${m.id}`}
            onClick={() => setDetailOpen(true)}
            title="Click to view full message"
          >
            {/* header pill */}
            <div className="flex items-center justify-center gap-2 px-3 py-1.5 rounded-t-xl border border-[#FFD166]/15 bg-black/30 text-xs text-[#B9C8D5] group-hover:border-[#FFD166]/30 group-hover:bg-black/40 transition-colors">
              {m.channel === "sms" ? <MessageSquare className="w-3 h-3 text-[#FFD166] shrink-0" /> : <Mail className="w-3 h-3 text-[#FFD166] shrink-0" />}
              <span className="text-[#FFD166]/80 font-medium">{m.subject || "Automated"}</span>
              <span className="text-[#B9C8D5]/50">·</span>
              <span className={`font-medium ${statusOk ? "text-emerald-400" : m.status === "failed" ? "text-red-400" : "text-[#B9C8D5]"}`}>
                {statusOk ? "✓ Sent" : m.status === "failed" ? "✗ Failed" : m.status}
              </span>
              <span className="text-[#B9C8D5]/50 ml-auto shrink-0">{m.created_at ? new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : ""}</span>
              <ChevronRight className="w-3 h-3 text-[#B9C8D5]/40 group-hover:text-[#FFD166]/60 shrink-0 transition-colors" />
            </div>
            {/* body preview */}
            {m.body && (
              <div className="px-3 py-2 rounded-b-xl border-x border-b border-[#FFD166]/10 group-hover:border-[#FFD166]/20 bg-black/20 text-xs text-[#B9C8D5] whitespace-pre-wrap break-words italic leading-relaxed transition-colors" data-testid={`system-msg-body-${m.id}`}>
                {m.body.length > 120 ? m.body.slice(0, 120) + "…" : m.body}
              </div>
            )}
          </div>
        ) : (
          <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm border ${isCustomer ? "bg-[#FFD166]/10 border-[#FFD166]/30 text-[#F6F1E4]" : "bg-emerald-500/10 border-emerald-500/30 text-[#F6F1E4]"}`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#B9C8D5] mb-1">
              <span className={isCustomer ? "text-[#FFD166]" : "text-emerald-300"}>{senderLabel}</span>
              <span className="inline-flex items-center gap-0.5">
                {m.channel === "sms" ? <MessageSquare className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                {m.channel === "sms" ? "SMS" : "Email"}
              </span>
            </div>
            {m.subject && <div className="text-xs font-medium text-[#FFD166] mb-0.5">{m.subject}</div>}
            <div className="whitespace-pre-wrap break-words">{m.body}</div>
            <div className="flex items-center gap-2 mt-1 text-[10px]">
              <span className="text-[#B9C8D5]/70">{fmtWhen(m.created_at)}</span>
              <span className={STATUS_STYLES[m.status] || "text-[#B9C8D5]"} data-testid={`msg-status-${m.id}`}>
                {m.status === "received" ? "Replied" : m.status}{m.simulated ? " (simulated)" : ""}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export const ConversationPanel = ({ reservationId, customerName, onClose, inline = false }) => {
  const [thread, setThread] = useState(null);
  const [channel, setChannel] = useState("sms");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const bottomRef = useRef(null);
  const channelPicked = useRef(false);

  const load = useCallback(async (initial = false) => {
    try {
      const { data } = await axios.get(`${API}/comms/reservations/${reservationId}/thread`);
      setThread(data);
      if (!channelPicked.current) {
        setChannel(data.sms_allowed ? "sms" : "email");
        channelPicked.current = true;
      }
    } catch (e) {
      if (initial) {
        toast.error(e.response?.data?.detail || "Couldn't open the conversation.");
        onClose?.();
      }
    }
    // eslint-disable-next-line
  }, [reservationId]);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 8000);
    return () => clearInterval(t);
  }, [load]);

  const msgCount = thread?.messages?.length || 0;
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgCount]);

  const applyTemplate = (tpl) => {
    const fn = thread?.reservation?.customer_first_name || "there";
    const eta = thread?.eta_min ? `${thread.eta_min} minutes` : "[ETA]";
    setBody(tpl.body.split("[Customer First Name]").join(fn).split("[ETA]").join(eta));
    setTplOpen(false);
  };

  const insertTracking = () => {
    if (!thread?.tracking_url) return;
    setBody((b) => (b ? b.trimEnd() + " " : "") + `Track your driver live: ${thread.tracking_url}`);
  };

  const send = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/comms/reservations/${reservationId}/send`, { channel, body: body.trim() });
      setBody("");
      setThread((t) => (t ? { ...t, messages: [...t.messages, data.message] } : t));
      if (!data.ok) toast.error("Message could not be delivered — try the other channel.");
      else toast.success(channel === "sms" ? "Text sent" : "Email sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the message.");
    } finally {
      setSending(false);
    }
  };

  const smsAllowed = !!thread?.sms_allowed;
  const contact = thread?.contact; // admin only

  // ── Content (shared between modal and inline) ─────────────────────────────
  const header = (
    <div className={`${inline ? "pb-3 mb-1 border-b border-[#FFD166]/15" : "p-4 border-b border-[#FFD166]/15"} flex items-start gap-3`}>
      <div className="flex-1 min-w-0">
        {!inline && <div className="eyebrow text-[#FFD166]">Contact Customer</div>}
        <div className={`font-serif ${inline ? "text-lg" : "text-xl"} text-[#F6F1E4] truncate`} data-testid="conv-customer-name">{customerName || thread?.reservation?.customer_first_name || "Guest"}</div>
        <div className="text-[11px] text-[#B9C8D5] mt-0.5 flex items-center gap-2 flex-wrap">
          {thread?.reservation?.short_code && <span className="font-mono text-[#FFD166]">{thread.reservation.short_code}</span>}
          {thread?.reservation?.delivery_resort && <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{thread.reservation.delivery_resort}</span>}
        </div>
        {thread && !contact && (
          <div className="mt-1 text-[10px] text-[#B9C8D5] inline-flex items-center gap-1" data-testid="conv-privacy-note">
            <ShieldCheck className="w-3 h-3 text-emerald-300" /> Guest contact info stays private — messages relay through our business line.
          </div>
        )}
        {contact && (
          <div className="mt-2 text-[11px] text-[#B9C8D5] space-y-0.5 border border-[#FFD166]/15 rounded-lg p-2 bg-black/25" data-testid="conv-admin-contact">
            <div>📞 {contact.phone || "—"} · ✉️ {contact.email || "—"}</div>
            <div className={contact.sms_consent ? "text-emerald-300" : "text-[#F4A93A]"}>
              {contact.sms_consent ? "✓ SMS opted in" : "✗ SMS not opted in — email only"}
              {contact.sms_consent_at && <span className="text-[#B9C8D5]"> · {fmtWhen(contact.sms_consent_at)} ({contact.sms_consent_source === "website_checkout" ? "website checkout" : contact.sms_consent_source || "—"})</span>}
            </div>
            {contact.last_outbound_at && <div>Last outbound · {fmtWhen(contact.last_outbound_at)}</div>}
            {contact.last_inbound_at && <div>Last customer reply · {fmtWhen(contact.last_inbound_at)}</div>}
          </div>
        )}
      </div>
      {!inline && onClose && <button onClick={onClose} className="text-[#B9C8D5] hover:text-[#FFD166]" data-testid="conv-close"><X className="w-5 h-5" /></button>}
    </div>
  );

  const messageArea = (
    <div className={`${inline ? "flex-1 overflow-y-auto py-3 space-y-2 min-h-[160px] max-h-[40vh]" : "flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]"}`} data-testid="conv-messages">
      {!thread && <div className="flex items-center justify-center py-10 text-[#B9C8D5]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading conversation…</div>}
      {thread && thread.messages.length === 0 && (
        <div className="text-center text-[#B9C8D5] text-sm py-8" data-testid="conv-empty">No messages yet — start the conversation below.</div>
      )}
      {thread?.messages.map((m) => <Bubble key={m.id} m={m} />)}
      <div ref={bottomRef} />
    </div>
  );

  const composer = (
    <div className={`${inline ? "pt-3 border-t border-[#FFD166]/15 space-y-2" : "p-4 border-t border-[#FFD166]/15 space-y-2"}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-full border border-[#FFD166]/25 overflow-hidden">
          {smsAllowed && (
            <button
              type="button"
              onClick={() => setChannel("sms")}
              className={`px-3 py-1.5 text-xs inline-flex items-center gap-1 ${channel === "sms" ? "bg-[#FFD166] text-[#0F1E2C] font-semibold" : "text-[#FFD166]"}`}
              data-testid="conv-channel-sms"
            >
              <MessageSquare className="w-3 h-3" /> Text
            </button>
          )}
          <button
            type="button"
            onClick={() => setChannel("email")}
            className={`px-3 py-1.5 text-xs inline-flex items-center gap-1 ${channel === "email" ? "bg-[#FFD166] text-[#0F1E2C] font-semibold" : "text-[#FFD166]"}`}
            data-testid="conv-channel-email"
          >
            <Mail className="w-3 h-3" /> Email
          </button>
        </div>
        {thread && !smsAllowed && (
          <span className="text-[10px] text-[#F4A93A]" data-testid="conv-no-sms-note">Guest did not opt into SMS — email only</span>
        )}
        {thread && smsAllowed && channel === "sms" && !thread.sms_configured && (
          <span className="text-[10px] text-[#F4A93A]" data-testid="conv-sms-simulated-note">SMS provider not connected yet — texts are simulated</span>
        )}
        <div className="relative ml-auto">
          <button type="button" onClick={() => setTplOpen((o) => !o)} className="text-xs text-[#FFD166] inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-[#FFD166]/25 hover:bg-[#FFD166]/10" data-testid="conv-templates-btn">
            Quick messages <ChevronDown className="w-3 h-3" />
          </button>
          {tplOpen && (
            <div className="absolute right-0 bottom-full mb-1 w-60 rounded-xl border border-[#FFD166]/25 bg-[#0F1E2C] shadow-xl z-10 overflow-hidden" data-testid="conv-templates-menu">
              {TEMPLATES.map((t) => (
                <button key={t.key} type="button" onClick={() => applyTemplate(t)} className="block w-full text-left px-3 py-2 text-xs text-[#F6F1E4] hover:bg-[#FFD166]/10" data-testid={`conv-template-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        placeholder={channel === "sms" ? "Type a text message…" : "Type an email message…"}
        className="w-full rounded-xl bg-black/30 border border-[#FFD166]/25 px-3 py-2 text-sm text-[#F6F1E4] focus:outline-none focus:border-[#FFD166]/60 resize-none"
        data-testid="conv-message-input"
      />
      <div className="flex items-center gap-2">
        {thread?.tracking_url && (
          <button type="button" onClick={insertTracking} className="text-xs text-[#FFD166] inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border border-[#FFD166]/25 hover:bg-[#FFD166]/10" data-testid="conv-insert-tracking">
            <MapPin className="w-3 h-3" /> Insert Tracking Link
          </button>
        )}
        <button
          type="button"
          onClick={send}
          disabled={sending || !body.trim() || !thread}
          className="btn-primary ml-auto inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="conv-send-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send {channel === "sms" ? "Text" : "Email"}
        </button>
      </div>
    </div>
  );

  // ── Inline mode (embedded in drawer tab) ──────────────────────────────────
  if (inline) {
    return (
      <div className="flex flex-col h-full" data-testid="conversation-panel-inline">
        {header}
        {messageArea}
        {composer}
      </div>
    );
  }

  // ── Modal mode (full-screen overlay) ─────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[130] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="conversation-panel">
      <div className="glass w-full max-w-lg max-h-[92vh] flex flex-col rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-[#FFD166]/15 flex items-start gap-3">
          {header}
        </div>

        {/* Messages */}
        {messageArea}

        {/* Composer */}
        {composer}
      </div>
    </div>
  );
};

export default ConversationPanel;
